// pages/OrientationPage.jsx — Pré-onboarding PUBLIC (spec CTO §4-6).
// Aucun compte requis, aucune donnée métier créée. Question métier commune → 3
// questions dynamiques → recommandation déterministe (stockée en sessionStorage)
// → /orientation/resultat. Animé (skill « animate »), charte or/crème/encre.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OnboardingPage.css";
import {
  ORIENTATION_PROFILES,
  ORIENTATION_QUESTIONS,
  recommendPlan,
  saveOrientation,
} from "../lib/orientation";

const PROFILE_FR = {
  collectionneur: "Collectionneur ou propriétaire d'œuvres",
  advisor: "Art Advisor ou gestionnaire de collections",
  family_office: "Family Office ou conseil patrimonial",
  fondation: "Fondation ou institution",
};

const Q_FR = {
  collectionneur: {
    artworks_count: { label: "Combien d'œuvres gérez-vous environ ?", opts: { lt50: "Moins de 50", b50_200: "50 à 200", b200_1000: "200 à 1 000", gt1000: "Plus de 1 000" } },
    needs: { label: "Que souhaitez-vous faire avec ARTKIV ?", opts: { inventory: "Inventorier mes œuvres et documents", market: "Suivre leur valeur et le marché", ai_ocr: "Utiliser l'OCR et les analyses IA", insurance_conservation: "Préparer assurance et conservation", succession: "Préparer succession et transmission", certification: "Certifier et tracer les œuvres" } },
    collaboration: { label: "Qui doit accéder à la collection ?", opts: { me_only: "Moi uniquement", one_reader: "Une personne supplémentaire en lecture", multiple: "Plusieurs collaborateurs ou experts" } },
  },
  advisor: {
    clients_count: { label: "Combien de clients ou collections supervisez-vous ?", opts: { b1_5: "1 à 5", b6_20: "6 à 20", b21_50: "21 à 50", gt50: "Plus de 50" } },
    launch_ai: { label: "Souhaitez-vous lancer vous-même les analyses IA et OCR pour vos clients ?" },
    pro_tools: { label: "Quels outils professionnels vous sont nécessaires ?", opts: { custom_reports: "Rapports personnalisés", white_label: "Rapports en marque blanche", team: "Gestion d'une équipe", unlimited_clients: "Clients illimités", mission_tracking: "Suivi administratif des missions" } },
  },
  family_office: {
    families_count: { label: "Combien de familles devez-vous superviser ?", opts: { b1_3: "1 à 3", b4_10: "4 à 10", gt10: "Plus de 10" } },
    inventory_state: { label: "Les collections sont-elles déjà inventoriées ?", opts: { none: "Non, nous partons de zéro", csv: "Oui, dans Excel ou CSV", pdf: "Oui, dans des PDF ou catalogues", other_software: "Oui, dans un autre logiciel" } },
    priorities: { label: "Quels sont vos besoins prioritaires ?", opts: { overview: "Vue patrimoniale consolidée", valuation_reporting: "Valorisation et reporting", insurance_risk: "Assurance et gestion des risques", succession: "Succession et transmission", rights_actors: "Gestion des droits et intervenants", erp: "Export vers un ERP ou autre outil" } },
  },
  fondation: {
    collections_count: { label: "Combien de collections gérez-vous ?", opts: { one: "Une", b2_5: "2 à 5", gt5: "Plus de 5" } },
    collaborators_count: { label: "Quelle est la taille de votre équipe ?", opts: { solo: "Une personne", b2_5: "2 à 5 personnes", gt5: "Plus de 5 personnes" } },
    priorities: { label: "Quelles sont vos priorités ?", opts: { inventory: "Inventaire et documentation", conservation: "Conservation", loans: "Prêts et mouvements", exhibitions: "Expositions et médiation", ocr_import: "OCR et import de catalogues", reports_governance: "Rapports et gouvernance" } },
  },
};

// ── Styles (charte ARTKIV) ──────────────────────────────────────────────────
const page = { minHeight: "100vh", background: "#f9f7f4", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1rem", fontFamily: "Georgia, 'Times New Roman', serif" };
const card = { width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 20, padding: "2.5rem" };
const eyebrow = { fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#a9843f", margin: 0 };
const h1 = { fontSize: "1.9rem", lineHeight: 1.2, color: "#1a1a1a", margin: "0.75rem 0 0.75rem" };
const pMuted = { fontSize: "0.98rem", lineHeight: 1.6, color: "#6b6459", margin: "0 0 1.75rem" };
const qLabelStyle = { fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#8a8378", margin: "0 0 0.9rem" };
const qTitle = { fontSize: "1.5rem", lineHeight: 1.25, color: "#1a1a1a", margin: "0 0 0.25rem" };
const optionBtn = { textAlign: "left", padding: "0.85rem 1rem", borderRadius: 12, border: "1px solid #e5e0d8", background: "#fff", color: "#1a1a1a", fontSize: "0.95rem", cursor: "pointer", fontFamily: "inherit", transition: "border-color .15s, background .15s" };
const optionSel = { ...optionBtn, borderColor: "#c9a961", background: "#faf6ec" };
const progressRow = { marginBottom: "1.75rem" };
const track = { height: 4, width: "100%", background: "#efe9dc", borderRadius: 999, overflow: "hidden", marginTop: 8 };
const fill = { height: "100%", background: "#c9a961", borderRadius: 999, transition: "width 0.3s ease" };
const navRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.75rem" };
const ghost = { padding: "0.65rem 1rem", borderRadius: 10, border: "none", background: "transparent", color: "#6b6459", fontSize: "0.9rem", cursor: "pointer", fontFamily: "inherit" };
const primary = { padding: "0.8rem 1.5rem", borderRadius: 10, border: "none", background: "#c9a961", color: "#1a1a1a", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "inherit" };
const primaryOff = { ...primary, opacity: 0.45, cursor: "default" };

export default function OrientationPage() {
  const { t } = useTranslation();
  const tr = (k, fr, opt) => t(k, { defaultValue: fr, ...(opt || {}) });
  const navigate = useNavigate();

  const [profileType, setProfileType] = useState(null);
  const [step, setStep] = useState("intro"); // "intro" | 0..n
  const [answers, setAnswers] = useState({});

  const questions = profileType ? ORIENTATION_QUESTIONS[profileType] : [];

  const chooseProfile = (key) => { setProfileType(key); setAnswers({}); setStep(0); };
  const setOne = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));
  const toggleMulti = (id, val) =>
    setAnswers((a) => {
      const cur = a[id] || [];
      return { ...a, [id]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });

  const finish = () => {
    const rec = recommendPlan(profileType, answers);
    saveOrientation(profileType, answers, rec);
    navigate("/orientation/resultat");
  };

  const qMeta = typeof step === "number" ? questions[step] : null;
  const isLast = typeof step === "number" && step === questions.length - 1;
  const next = () => (isLast ? finish() : setStep(step + 1));
  const back = () => (step === 0 ? setStep("intro") : setStep(step - 1));

  const qFr = qMeta ? Q_FR[profileType][qMeta.id] : null;
  const answered = !qMeta
    ? false
    : qMeta.type === "multi"
    ? true // choix multiples facultatifs
    : answers[qMeta.id] !== undefined && answers[qMeta.id] !== null && answers[qMeta.id] !== "";

  return (
    <div style={page}>
      <div style={card}>
        {step === "intro" ? (
          <div className="ob-stagger" key="intro">
            <p style={eyebrow}>ARTKIV</p>
            <h1 style={h1}>{tr("orientation.intro.title", "Trouvons l'espace ARTKIV adapté à votre activité")}</h1>
            <p style={pMuted}>{tr("orientation.intro.micro", "Répondez à quelques questions. Aucune inscription n'est nécessaire pour découvrir notre recommandation.")}</p>
            <p style={qLabelStyle}>{tr("orientation.common.label", "Vous utilisez ARTKIV principalement comme :")}</p>
            <div style={{ display: "grid", gap: "0.6rem" }}>
              {ORIENTATION_PROFILES.map((p) => (
                <button key={p.key} style={optionBtn} onClick={() => chooseProfile(p.key)}>
                  {tr(`orientation.profiles.${p.key}`, PROFILE_FR[p.key])}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div style={progressRow}>
              <span style={eyebrow}>
                {tr("orientation.progress", "Question {{current}} sur {{total}}", { current: step + 1, total: questions.length })}
              </span>
              <div style={track}>
                <div style={{ ...fill, width: `${((step + 1) / questions.length) * 100}%` }} />
              </div>
            </div>

            <div className="ob-in" key={step}>
              <h2 style={qTitle}>
                {tr(`orientation.questions.${profileType}.${qMeta.id}.label`, qFr.label)}
              </h2>

              <div style={{ margin: "1.25rem 0 0.5rem", display: "grid", gap: "0.55rem" }}>
                {qMeta.type === "boolean" ? (
                  <div style={{ display: "flex", gap: "0.6rem" }}>
                    {[{ v: true, fr: "Oui" }, { v: false, fr: "Non" }].map((o) => (
                      <button
                        key={String(o.v)}
                        style={answers[qMeta.id] === o.v ? optionSel : optionBtn}
                        onClick={() => setOne(qMeta.id, o.v)}
                      >
                        {tr(o.v ? "orientation.yes" : "orientation.no", o.fr)}
                      </button>
                    ))}
                  </div>
                ) : (
                  qMeta.opts.map((optKey) => {
                    const selected = qMeta.type === "multi" ? (answers[qMeta.id] || []).includes(optKey) : answers[qMeta.id] === optKey;
                    return (
                      <button
                        key={optKey}
                        style={selected ? optionSel : optionBtn}
                        onClick={() => (qMeta.type === "multi" ? toggleMulti(qMeta.id, optKey) : setOne(qMeta.id, optKey))}
                      >
                        {tr(`orientation.questions.${profileType}.${qMeta.id}.opts.${optKey}`, qFr.opts[optKey])}
                      </button>
                    );
                  })
                )}
              </div>

              <div style={navRow}>
                <button style={ghost} onClick={back}>{tr("orientation.back", "Retour")}</button>
                <button
                  style={answered ? primary : primaryOff}
                  onClick={answered ? next : undefined}
                  disabled={!answered}
                >
                  {isLast ? tr("orientation.finish", "Voir ma recommandation") : tr("orientation.continue", "Continuer")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
